import React, { useState, useEffect, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { translatedMessage } from '../../service/LanguageService';
import GeneralUtils from '../../utilities/GeneralUtils';
import DataTableUtils from '../../utilities/DataTableUtils';
import useAuth from "../../hooks/useAuth";
import DeleteModalComponent from '../../components/DeleteModalComponent';
import EnumService from '../../service/EnumService';
import RecruitmentDataTableUtils from '../../utilities/RecruitmentDataTableUtils';
import { RecruitmentService } from '../../service/RecruitmentService';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessageDisplayComponent from '../../components/ErrorMessageDisplayComponent';
import RecruitmentPlanEdit from './RecruitmentPlanEdit';
import ValidationErrorModalComponent from '../../components/ValidationErrorModalComponent';
import { useNavigate } from 'react-router-dom';
import { InstitutionService } from '../../service/InstitutionService';

const RecruitmentPlanList = () => {
    let emptyRecruitmentPlan = {
        id: null,
        recruitmentCampaign: { id: null, name: '' },
        institution: { id: null, name: '' }
    };

    const [recruitmentPlans, setRecruitmentPlans] = useState([]);
    const [statusValues, setStatusValues] = useState([]);
    const [recruitmentPlan, setRecruitmentPlan] = useState(null);
    const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [firstLoading, setFirstLoading] = useState(true);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('generic.error.get.data');
    const [showValidationErrorDialog, setShowValidationErrorDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [recruitmentCampaigns, setRecruitmentCampaigns] = useState([]);

    const disableEdit = false;

    const toast = useRef(null);
    const dt = useRef(null);
    const { auth } = useAuth();
    const navigate = useNavigate();

    const recruitmentService = useMemo(() => new RecruitmentService(), []);
    const institutionService = useMemo(() => new InstitutionService(), []);

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: DataTableUtils.defalRowsPerPage(),
        page: 1,
        sortField: 'id',
        sortOrder: 1,
        filters: {
            "institution.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
            "recruitmentCampaign.name": { value: null, matchMode: FilterMatchMode.CONTAINS },
            status: { value: null, matchMode: FilterMatchMode.IN }
        }
    });
    const [lazyParamsUpdatedFromSession, setLazyParamsUpdatedFromSession] = useState(false);
    const propToColumnMap = {
        "institution.name": 'i.name',
        "recruitmentCampaign.name": 'rc.name',
        status: 'status',
        lastModifiedOn: 'last_updated_on'
    };
    const [totalRecords, setTotalRecords] = useState(0);

    const hasPermission = (permissionName) => {
        if (auth && auth.user && auth.user.permissions) {
            return auth.user.permissions.indexOf(permissionName) > -1;
        } else {
            return false;
        }
    };

    useEffect(() => {
        setFirstLoading(true);

        const getEnumValues = async () => {
            const response = await EnumService.getEnumByName("RecruitmentPlanStatus");
            setStatusValues(response)
        }

        const getRecruitmentCampaigns = async () => {
            await recruitmentService.getRecruitmentCampaignList({})
                .then((_data) => {
                    let _campaigns = []
                    _data.forEach(item => {
                        _campaigns.push({ label: item.name, value: item.name })
                    })
                    setRecruitmentCampaigns(_campaigns);
                })
                .catch((error) => toast.current.show({ severity: 'error', summary: translatedMessage(error), life: 5000 }));
        };

        getRecruitmentPlans()
        getEnumValues()
        getRecruitmentCampaigns()

        setFirstLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recruitmentService]);

    useEffect(() => {
        getRecruitmentPlans()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lazyParams]);

    const setCriteria = () => {
        GeneralUtils.processSessionFilterValues('RecruitmenPlanList', lazyParams, lazyParamsUpdatedFromSession, setLazyParamsUpdatedFromSession);

        let criteria = {};
        criteria.sortOrder = lazyParams.sortOrder < 0 ? 'DESC' : 'ASC';
        criteria.sortField = propToColumnMap[lazyParams.sortField] || 'id';
        criteria.institutionName = lazyParams.filters["institution.name"].value;
        criteria.campaignName = lazyParams.filters["recruitmentCampaign.name"].value;
        criteria.statuses = lazyParams.filters.status.value;


        return criteria;
    };

    const getRecruitmentPlans = async () => {
        setIsLoading(true);

        let criteria = setCriteria();
        criteria.startRow = lazyParams.first;
        criteria.pageSize = lazyParams.rows;

        // console.log(criteria)

        await recruitmentService.getRecruitmentPlanList(criteria)
            .then((_data) => {
                // console.log(_data)
                setRecruitmentPlans(_data.items);
                setTotalRecords(_data.totalCount);
                setIsLoading(false);
            })
            .catch((error) => {
                setError(true);
                setErrorMessage(error);
                // toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 5000 })
            });
    };

    const showEditRecruitmentPlan = async () => {
        let _recruitmentPlan = emptyRecruitmentPlan

        await recruitmentService.getActiveCampaign()
            .then((_data) => {
                // console.log("data: ", _data)
                _recruitmentPlan.institution = auth?.user?.institution
                _recruitmentPlan.recruitmentCampaign = _data.recruitmentCampaign
                // console.log(_recruitmentPlan)
                setRecruitmentPlan(_recruitmentPlan);
                setEditDialogVisible(true);
            })
            .catch((error) => {
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 5000 })
                }
            });
    };

    const recruitmentPlanViewPage = (id) => {
        navigate(`/recruitment-plan/${id}`);
    };

    const showDeleteRecruitmentPlanDialog = (recruitmentPlan) => {
        setRecruitmentPlan(recruitmentPlan);
        setDeleteDialogVisible(true);
    };

    const hideDeleteRecruitmentPlanDialog = () => {
        setDeleteDialogVisible(false);
    };

    const deleteRecruitmentPlan = () => {
        recruitmentService.deleteRecruitmentPlan(recruitmentPlan.id)
            .then(
                () => {
                    getRecruitmentPlans()
                    toast?.current?.show({ severity: 'success', summary: translatedMessage('generic.delete.success'), contentClassName: "pcn-toast-success" });
                },
                (error) => {
                    console.log(error)
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error), life: 7000 });
                }
            ).finally(() => {
                setDeleteDialogVisible(false);
                setRecruitmentPlan(null);
            });
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    {auth?.user?.institutionResponsible && <>
                        <Button label={translatedMessage('recruitment.plan.add')}
                            icon="pi pi-plus"
                            className="p-button-primary mr-2"
                            onClick={showEditRecruitmentPlan}
                            disabled={disableEdit}
                        />

                        <Button label={translatedMessage('institution.jobs.sync')}
                            icon="pi pi-sync" className="p-button-primary mr-2" onClick={() => syncIntitutionJobs()} disabled={isLoading}
                        />
                    </>}
                </div>
            </React.Fragment>
        );
    };

    // const handleExport = () => {
    //     let exportData = recruitmentCampaigns.map(item => ({ ...item }));
    //     exportData.map(item => {
    //         let exportItem = item
    //         exportItem.status = translatedMessage("RecruitmentStatus." + item.status)
    //         exportItem.lastUpdatedOn = new Date(Date.parse(item.lastUpdatedOn))
    //         delete exportItem.folderId
    //         delete exportItem.fileUuid
    //         delete exportItem.fileName
    //         delete exportItem.description

    //         return item;
    //     })

    //     return exportData
    // }

    const rightToolbarTemplate = () => {
        return (
            <></>
            // <ExportButtonComponent
            //     getExportData={handleExport}
            //     header={tableHeader}
            //     fileName={translatedMessage("recruitment.campaign")}                    
            // />
        );
    };

    const syncIntitutionJobs = () => {
        setIsLoading(true);
        institutionService.syncIntitutionJobs().then(
            () => {
                setIsLoading(false);
                toast?.current?.show({ severity: 'success', summary: translatedMessage("institution.sync.success"), contentClassName: "pcn-toast-success" });
            },
            (error) => {
                // console.log(error)
                if (error?.hasValidationError) {
                    setValidationErrors(error.errorCodes)
                    setShowValidationErrorDialog(true)
                } else {
                    error && toast?.current?.show({ severity: 'error', summary: translatedMessage(error.errorMessage), life: 7000 })
                }
                setIsLoading(false);
            }
        );
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="actions flex flex-wrap align-items-center justify-content-end">
                {(hasPermission('RECRUITMENT_PLAN_VIEW') || auth?.user?.institutionResponsible) &&
                    <Button icon="pi pi-eye" className="p-button-rounded p-button-primary m-1" onClick={() => recruitmentPlanViewPage(rowData?.id)}
                        tooltip={translatedMessage("generic.view")} tooltipOptions={{ showOnDisabled: true, position: 'top' }} />
                }
                {auth?.user?.institutionResponsible && <>
                    <Button icon="pi pi-trash" className="p-button-rounded p-button-danger m-1" onClick={() => showDeleteRecruitmentPlanDialog(rowData)}
                        tooltip={translatedMessage("generic.delete")} tooltipOptions={{ showOnDisabled: true, position: 'top' }}
                        disabled={rowData.status !== 'DRAFT'} />
                </>}
            </div>
        );
    };

    const onPage = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.first = event.first;
        _lazyParams.page = event.page;
        _lazyParams.rows = event.rows;
        setLazyParams(_lazyParams);
    };

    const onSort = (event) => {
        let _lazyParams = { ...lazyParams };
        _lazyParams.sortField = event.sortField;
        _lazyParams.sortOrder = event.sortOrder;
        setLazyParams(_lazyParams);
    };

    const onFilter = (event) => {
        event['first'] = 0;
        setLazyParams(event);
    };


    if (firstLoading) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ProgressSpinner />
            </div>
        );
    } else if (error) {
        return (
            <div className="w-full flex align-items-center">
                <Toast ref={toast} />
                <ErrorMessageDisplayComponent message={errorMessage} />
            </div>
        );
    } else {
        return (
            <>
                <Toast ref={toast} />
                <div className="grid h-full">
                    <div className="col-12">
                        <div className="card h-full">
                            <h3 className='mb-2'>{translatedMessage('recruitment.plans')}</h3>

                            <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} className="px-0 pt-0"></Toolbar>

                            <DataTable
                                ref={dt}
                                value={recruitmentPlans}
                                dataKey="id"
                                paginator
                                lazy
                                totalRecords={totalRecords}
                                first={lazyParams.first}
                                rows={lazyParams.rows}
                                sortField={lazyParams.sortField}
                                sortOrder={lazyParams.sortOrder}
                                onPage={onPage}
                                onSort={onSort}
                                onFilter={onFilter}
                                rowsPerPageOptions={DataTableUtils.rowsPerPageOptions()}
                                className="datatable-responsive pcn-datatable"
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate={'{first} - {last} ' + translatedMessage('generic.of') + ' {totalRecords}'}
                                emptyMessage={translatedMessage('generic.tableEmptyMessage')}
                                responsiveLayout="scroll"
                                filterDisplay="row"
                                filters={lazyParams.filters}
                                loading={isLoading}
                            >
                                <Column
                                    field="institution.name"
                                    header={translatedMessage('institution.institution')}
                                    sortable
                                    headerStyle={{ width: '20%', minWidth: '10rem' }}
                                    filter
                                    showFilterMenu={false}
                                />
                                <Column
                                    field="recruitmentCampaign.name"
                                    header={translatedMessage('recruitment.campaign')}
                                    sortable
                                    headerStyle={{ width: '20%', minWidth: '10rem' }}
                                    filter
                                    filterElement={(e) => RecruitmentDataTableUtils.planCampaignsFilterTemplate(e, recruitmentCampaigns)}
                                    showFilterMenu={false}
                                />
                                <Column
                                    field="status"
                                    header={translatedMessage('generic.status')}
                                    sortable
                                    body={(e) => RecruitmentDataTableUtils.planStatusBodyTemplate(e.status)}
                                    headerStyle={{ width: '20%', minWidth: '8rem' }}
                                    filter
                                    filterElement={(e) => RecruitmentDataTableUtils.planStatusFilterTemplate(e, statusValues)}
                                    showFilterMenu={false}
                                ></Column>
                                <Column
                                    field="lastModifiedOn"
                                    header={translatedMessage('generic.lastUpdated.on')}
                                    sortable
                                    body={(e) => GeneralUtils.formatDate(e.lastModifiedOn)}
                                    headerStyle={{ width: '10%', minWidth: '8rem' }}
                                ></Column>
                                <Column body={actionBodyTemplate}></Column>
                            </DataTable>

                            {/* Edit dialog */}
                            {editDialogVisible &&
                                <RecruitmentPlanEdit
                                    value={recruitmentPlan}
                                    visible={editDialogVisible}
                                    afterSave={getRecruitmentPlans}
                                    visibleSetter={setEditDialogVisible}
                                />
                            }

                            {deleteDialogVisible &&
                                <DeleteModalComponent
                                    visible={deleteDialogVisible}
                                    item={recruitmentPlan?.name}
                                    closeDialog={hideDeleteRecruitmentPlanDialog}
                                    deleteRecord={() => deleteRecruitmentPlan()}
                                />
                            }

                            {showValidationErrorDialog &&
                                <ValidationErrorModalComponent
                                    visible={showValidationErrorDialog}
                                    errors={validationErrors}
                                    dialogTitle={translatedMessage("recruitment.campaign.validation.title")}
                                    closeDialog={() => setShowValidationErrorDialog(false)}
                                />
                            }
                        </div>
                    </div>
                </div>
            </>
        );
    }
};

export default RecruitmentPlanList;
